import { css } from '@emotion/react';
import { ButtonLink } from '@/components/shared/ButtonLink';
import { pagesPath } from '@/lib/pagesPath';
import { fadeIn } from '@/styles/keyframes';
import { getScreen } from '@/styles/media-query';
import { withPrefix } from 'gatsby';

export type PlanContentSectionProps = {
  className?: string;
  as?: keyof HTMLElementTagNameMap;
  supportFlg?: string;
};

export const PlanContentSection: React.FC<PlanContentSectionProps> = ({
  className,
  as,
  supportFlg,
}) => {
  return (
    <div
      className={className}
      css={css`
        max-width: 1120px;
        margin-right: auto;
        margin-left: auto;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 40px;
        @media ${getScreen('pc')} {
          gap: 25px 1%;
        }
      `}
    >
      <Plan1
        css={css`
          @media ${getScreen('pc')} {
            width: 24%;
          }
          @media (max-width: 1100px) {
            width: 49%;
          }
          @media ${getScreen('pc', '未満')} {
            width: 100%;
          }
        `}
        as={as}
      />
      <Plan2
        css={css`
          @media ${getScreen('pc')} {
            width: 24%;
          }
          @media (max-width: 1100px) {
            width: 49%;
          }
          @media ${getScreen('pc', '未満')} {
            width: 100%;
          }
        `}
        as={as}
      />
      <Plan3
        css={css`
          width: 100%;
          @media ${getScreen('pc')} {
            width: 24%;
          }
          @media (max-width: 1100px) {
            width: 49%;
          }
          @media ${getScreen('pc', '未満')} {
            width: 100%;
          }
        `}
        as={as}
      />
      <Plan4
        css={css`
          width: 100%;
          @media ${getScreen('pc')} {
            width: 24%;
          }
          @media (max-width: 1100px) {
            width: 49%;
          }
          @media ${getScreen('pc', '未満')} {
            width: 100%;
          }
        `}
        as={as}
      />
      <div
        css={css`
          position: relative;
          top: -10px;
          width: 100%;
          text-align: right;
          font-size: 12px;
        `}
      >
        <p>※月次でサポート内容を変更することも可能です</p>
        <p>※アカウント数は上限を100としてご利用いただけます</p>
      </div>
      
    {supportFlg !== "no" && 
      <Support
        css={css`
          grid-area: support;
          align-self: flex-start;
        `}
      />
    }
    </div>
  );
};

const Plan1: React.FC<PlanContentSectionProps> = ({ className, as }) => {
  return (
    <PlanSection
      className={className}
      as={as}
      title={
        <>スターター<br />プラン</>
      }
      readText={
        <>
          <strong>シード・アーリーフェーズ</strong>に<br />最適！
        </>
      }
      const1={
        <>
          <big>0</big><span>円</span>
        </>
      }
      const2={
        <>
          <big>50,000</big><span>円<br /><small>（税抜）</small></span>
        </>
      }
      feature={
        <>
          IPO準備にかかるコストを削減しつつ、
          <br />
          計画的に上場準備を進めたい
        </>
      }
      contactTo={pagesPath.estimate.$url()}
      caution={<>100アカウントご利用可能</>}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        `}
      >
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>Uniforce IPO準備クラウド<br />全機能の利用</span>
        </p>
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>IPO準備アドバイザーによる<br />徹底サポート</span>
          <small
            css={css`
              display: block;
              font-size: 12px;
              line-height: 1.0;
              color: #7B7B7B;
            `}
          >（カスタマーサポート）</small>
        </p>
      </div>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          color: #5F6D81;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
          `}
        >
          弊社コンサルによる<br />導入・伴走支援
        </dt>
        <dd
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 12px 20px 14px;
            min-height: 75.5px;
          `}
        >
          <span
            css={css`
              font: normal normal bold 32px/32px 'Noto Sans JP', sans-serif;
            `}
          >×</span>
        </dd>
      </dl>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          color: #5F6D81;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
          `}
        >
          公認会計士による<br />IPO準備コンサルティング
        </dt>
        <dd
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 12px 20px 14px;
            min-height: 75.5px;
          `}
        >
          <span
            css={css`
              font: normal normal bold 32px/32px 'Noto Sans JP', sans-serif;
            `}
          >×</span>
        </dd>
      </dl>
    </PlanSection>
  );
};

const Plan2: React.FC<PlanContentSectionProps> = ({ className, as }) => {
  return (
    <PlanSection
      className={className}
      as={as}
      title={
        <>スタンダード<br />プラン</>
      }
      readText={
        <>
          <strong>中長期的</strong>に<br />上場を目指している
        </>
      }
      const1={
        <>
          <big>150,000</big><span>円<br /><small>（税抜）</small></span>
        </>
      }
      const2={
        <>
          <big>100,000</big><span>円<br /><small>（税抜）</small></span>
        </>
      }
      feature={
        <>
          IPO準備にかかるコストを削減しつつ、
          <br />
          計画的に上場準備を進めたい
        </>
      }
      contactTo={pagesPath.estimate.$url()}
      caution={<>100アカウントご利用可能</>}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        `}
      >
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>Uniforce IPO準備クラウド<br />全機能の利用</span>
        </p>
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>IPO準備アドバイザーによる<br />徹底サポート</span>
          <small
            css={css`
              display: block;
              font-size: 12px;
              line-height: 1.0;
              color: #7B7B7B;
            `}
          >（カスタマーサポート）</small>
        </p>
      </div>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          background: #f8fffe;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #00b3a7;
          `}
        >
          コンサルタントによる<br />導入・継続サポート支援
        </dt>
        <dd
          css={css`
            padding: 12px 32px 14px;
          `}
        >
          <ul
            css={css`
              list-style: none;
              text-align: left;
              max-width: 230px;
              margin: 26px auto 32px;
              margin-left: auto;
              margin-right: auto;
              padding-bottom: 0;
              max-width: 100%;
              padding: 1px 0;
              margin: 0;
            `}
          >
            {[
              '初月の導入サポート',
              <>
                <p>年4回のサポート</p>
              </>,
            ].map((item, i) => (
              <li
                css={css`
                  position: relative;
                  text-align: left;
                  padding-inline-start: 1.4em;
                  font: normal normal 500 12px/17px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.12px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background-repeat: no-repeat;
                    background-image: url(${withPrefix('/images/list-check-icon.svg')});
                  }
                `}
                key={i}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          background: #f8fffe;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #00b3a7;
          `}
        >
          公認会計士による<br />IPO準備コンサルティング
        </dt>
        <dd
          css={css`
            padding: 12px 20px 14px;
          `}
        >
          <ul
            css={css`
              list-style: none;
              text-align: left;
              max-width: 230px;
              margin: 26px auto 32px;
              margin-left: auto;
              margin-right: auto;
              padding-bottom: 0;
              max-width: 100%;
              padding: 1px 0;
              margin: 0;
            `}
          >
            {[
              '初月の導入サポート',
              <>
                <p>年4回のサポート</p>
              </>,
            ].map((item, i) => (
              <li
                css={css`
                  position: relative;
                  text-align: left;
                  padding-inline-start: 1.4em;
                  font: normal normal 500 12px/17px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.12px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background-repeat: no-repeat;
                    background-image: url(${withPrefix('/images/list-check-icon.svg')});
                  }
                `}
                key={i}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
    </PlanSection>
  );
};

const Plan3: React.FC<PlanContentSectionProps> = ({ className, as }) => {
  return (
    <PlanSection
      className={className}
      as={as}
      title="伴走支援プラン"
      readText={
        <>
          <p
            css={css`
              position: absolute;
              top: -40px;
              z-index: 5;
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 30px;
              padding: 5px 10px;
              border-radius: 20px;
              font-size: 14px;
              line-height: 1.0;
              color: #FE8321;
              background: #FFE8D6;
            `}
          >
            人気N0.1！
            <img
              css={css`
                position: absolute;
                right: 10px;
                bottom: -17px;
                z-index: 10;
                width: 20px;
              `}
              src={withPrefix('/images/plan/plan-human.png')}
              alt=""
            />
          </p>
          <strong>IPO準備経験者</strong>が<br /><strong>実務</strong>まで<strong>サポート！</strong>
        </>
      }
      const1={
        <>
          <big>150,000</big><span>円<br /><small>（税抜）</small></span>
        </>
      }
      const2={
        <>
          <big>200,000</big><span>円～<br /><small>（税抜）</small></span>
        </>
      }
      feature={
        <>
          IPO準備にかかるコストを削減しつつ、
          <br />
          計画的に上場準備を進めたい
        </>
      }
      contactTo={pagesPath.estimate.$url()}
      caution={<>100アカウントご利用可能</>}
      isAdvanced
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        `}
      >
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>Uniforce IPO準備クラウド<br />全機能の利用</span>
        </p>
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>IPO準備アドバイザーによる<br />徹底サポート</span>
          <small
            css={css`
              display: block;
              font-size: 12px;
              line-height: 1.0;
              color: #7B7B7B;
            `}
          >（カスタマーサポート）</small>
        </p>
      </div>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          background: #f8fffe;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #00b3a7;
          `}
        >
          コンサルタントによる<br />導入・継続サポート支援
        </dt>
        <dd
          css={css`
            padding: 12px 20px 14px;
          `}
        >
          <ul
            css={css`
              list-style: none;
              text-align: left;
              max-width: 230px;
              margin: 26px auto 32px;
              margin-left: auto;
              margin-right: auto;
              padding-bottom: 0;
              max-width: 100%;
              padding: 1px 0;
              margin: 0;
            `}
          >
            {[
              '初月の導入サポート',
              <>
                <p>年4回のサポート</p>
              </>,
            ].map((item, i) => (
              <li
                css={css`
                  position: relative;
                  text-align: left;
                  padding-inline-start: 1.4em;
                  font: normal normal 500 12px/17px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.12px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background-repeat: no-repeat;
                    background-image: url(${withPrefix('/images/list-check-icon.svg')});
                  }
                `}
                key={i}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          background: #f8fffe;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #00b3a7;
          `}
        >
          公認会計士による<br />IPO準備コンサルティング
        </dt>
        <dd
          css={css`
            padding: 12px 20px 14px;
          `}
        >
          <ul
            css={css`
              list-style: none;
              text-align: left;
              max-width: 230px;
              margin: 26px auto 32px;
              margin-left: auto;
              margin-right: auto;
              padding-bottom: 0;
              max-width: 100%;
              padding: 1px 0;
              margin: 0;
            `}
          >
            {[
              '内部体制構築支援',
              '規程類の整備',
              'BPO体制構築支援',
              <>
                <p>初月の導入サポート<br />など</p>
              </>,
            ].map((item, i) => (
              <li
                css={css`
                  position: relative;
                  text-align: left;
                  padding-inline-start: 1.4em;
                  font: normal normal 500 12px/17px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.12px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background-repeat: no-repeat;
                    background-image: url(${withPrefix('/images/list-check-icon.svg')});
                  }
                `}
                key={i}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
    </PlanSection>
  );
};

const Plan4: React.FC<PlanContentSectionProps> = ({ className, as }) => {
  return (
    <PlanSection
      className={className}
      as={as}
      title={
        <>フルパッケージ<br />プラン</>
      }
      readText={
        <>
          <strong>上場後の成長まで見据えた</strong><br />最上位プラン
        </>
      }
      const1={
        <>
          <big>Ask</big>
        </>
      }
      const2={
        <>
          <big>Ask</big>
        </>
      }
      feature={
        <>
          IPO準備にかかるコストを削減しつつ、
          <br />
          計画的に上場準備を進めたい
        </>
      }
      contactTo={pagesPath.estimate.$url()}
      caution={<>100アカウントご利用可能</>}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        `}
      >
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>Uniforce IPO準備クラウド<br />全機能の利用</span>
        </p>
        <p
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.6px;
            color: #00b3a7;
            text-align: center;
            padding: 20px 0 19px;
            background: #f8fffe;
            border: 1px solid #ade7e4;
            border-radius: 4px;
          `}
        >
          <span>IPO準備アドバイザーによる<br />徹底サポート</span>
          <small
            css={css`
              display: block;
              font-size: 12px;
              line-height: 1.0;
              color: #7B7B7B;
            `}
          >（カスタマーサポート）</small>
        </p>
      </div>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          background: #f8fffe;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #00b3a7;
          `}
        >
          コンサルタントによる<br />導入・継続サポート支援
        </dt>
        <dd
          css={css`
            padding: 12px 20px 14px;
          `}
        >
          <ul
            css={css`
              list-style: none;
              text-align: left;
              max-width: 230px;
              margin: 26px auto 32px;
              margin-left: auto;
              margin-right: auto;
              padding-bottom: 0;
              max-width: 100%;
              padding: 1px 0;
              margin: 0;
            `}
          >
            {[
              '初月の導入サポート',
              <>
                <p>年4回のサポート</p>
              </>,
            ].map((item, i) => (
              <li
                css={css`
                  position: relative;
                  text-align: left;
                  padding-inline-start: 1.4em;
                  font: normal normal 500 12px/17px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.12px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background-repeat: no-repeat;
                    background-image: url(${withPrefix('/images/list-check-icon.svg')});
                  }
                `}
                key={i}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
      <dl
        css={css`
          border: 1px solid #ade7e4;
          border-radius: 4px;
          width: 100%;
          margin-top: 18px;
          background: #f8fffe;
        `}
      >
        <dt
          css={css`
            padding: 11px 20px 9px;
            letter-spacing: 0.28px;
            border-bottom: 1px solid #00b3a7;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #00b3a7;
          `}
        >
          公認会計士による<br />IPO準備コンサルティング
        </dt>
        <dd
          css={css`
            padding: 12px 20px 14px;
          `}
        >
          <ul
            css={css`
              list-style: none;
              text-align: left;
              max-width: 230px;
              margin: 26px auto 32px;
              margin-left: auto;
              margin-right: auto;
              padding-bottom: 0;
              max-width: 100%;
              padding: 1px 0;
              margin: 0;
              
              a {
                color: #00B3A7;
                font-weight: normal;
                text-decoration: underline;
              }
            `}
          >
            {[
              '内部体制構築支援',
              '規程類の整備',
              'BPO体制構築支援',
              <>
                <p><a href="https://uniforce.co.jp/bpo-consulting/" target="_blank" rel="noreferrer">バックオフィス体制構築支援</a></p>
              </>,
              <>
                <p><a href="https://corp.uniforce.co.jp/" target="_blank" rel="noreferrer">エクイティ支援</a></p>
              </>,
              <>
                <p><a href="https://uniforce.co.jp/design/" target="_blank" rel="noreferrer">決算説明資料の作成支援</a></p>
              </>,
              <>
                <p><a href="https://uniforce.co.jp/disclosure/" target="_blank" rel="noreferrer">決算開示体制の整備</a></p>
              </>,
              <>
                <p>ブランディング支援<br />など</p>
              </>,
            ].map((item, i) => (
              <li
                css={css`
                  position: relative;
                  text-align: left;
                  padding-inline-start: 1.4em;
                  font: normal normal 500 12px/17px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.12px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background-repeat: no-repeat;
                    background-image: url(${withPrefix('/images/list-check-icon.svg')});
                  }
                `}
                key={i}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
    </PlanSection>
  );
};

const AdvancedPlan: React.FC<PlanContentSectionProps> = ({ className, as }) => {
  return (
    <PlanSection
      className={className}
      as={as}
      prefix={
        <div
          css={css`
            position: absolute;
            top: -15px;
            right: 7px;
            z-index: 1;
          `}
        >
          <img src={withPrefix('/images/plan-human.png')} alt="" width="35" height="102" />
        </div>
      }
      title="アドバンスドプラン"
      readText={
        <>
          IPO準備支援・実務経験が豊富な
          <br />
          公認会計士による<strong>コンサルティング</strong>付き
        </>
      }
      feature={
        <>
          IPO準備業務に精通した公認会計士に
          <br />
          相談しながら上場準備を進めたい
        </>
      }
      contactTo={pagesPath.estimate.$url()}
      caution={
        <>
          ご利用人数と公認会計士の
          <br />
          稼働工数により料金が変動いたします。
        </>
      }
      isAdvanced
    >
      <p
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
          letter-spacing: 0.6px;
          color: #00b3a7;
          text-align: center;
          padding: 20px 0 19px;
          background: #f8fffe;
          border: 1px solid #ade7e4;
          border-radius: 4px;
        `}
      >
        <span>公認会計士による導入サポート</span>
        <span>ベーシックプランの全機能</span>
      </p>
      <p
        css={css`
          margin: -16px 0 -12px;
          display: flex;
          justify-content: center;
        `}
      >
        <img
          src={withPrefix('/images/plan/plan-icon-plus.svg')}
          width="62"
          height="62"
          alt=""
        />
      </p>
      <dl
        css={css`
          border: 1px solid #fe8321;
          border-radius: 4px;
          width: 100%;
        `}
      >
        <dt
          css={css`
            padding: 16px 20px 12px;
            border-bottom: 1px solid #fe8321;
            text-align: center;
            font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #fe8321;
          `}
        >
          IPO準備支援・実務経験が豊富な
          <br />
          公認会計士によるコンサルティング
        </dt>
        <dd
          css={css`
            padding: 0 20px;
          `}
        >
          <dl
            css={css`
              padding: 16px 0 12px;
              border-bottom: 1px dotted #d1d8dd;
            `}
          >
            <dt
              css={css`
                padding: 3px;
                background: rgba(255, 234, 209, 0.4);
                border-radius: 9999px;
                text-align: center;
                font: normal normal 500 12px/16px 'Noto Sans JP', sans-serif;
                letter-spacing: 0.12px;
                color: #fe8321;
              `}
            >
              内容
            </dt>
            <dd
              css={css`
                margin-top: 12px;
                text-align: center;
                font: normal normal 500 14px/20px 'Noto Sans JP', sans-serif;
                letter-spacing: 0.14px;
                color: #444557;
              `}
            >
              15時間～ / 月のIPO準備に関する
              <br />
              プロジェクト進行支援
            </dd>
          </dl>
          <ul
            css={css`
              list-style: none;
              text-align: left;
              padding: 16px 0 24px;
              margin-right: -20px;
            `}
          >
            {[
              'Uniforceを使用した進行管理（PM業務）',
              'タスクの割り振り、管理業務',
              'IPO準備に関する質問への回答',
              'etc...',
            ].map((item) => (
              <li
                css={css`
                  position: relative;
                  padding-inline-start: 1.2em;
                  font: normal normal normal 14px/26px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.14px;
                  color: #444557;

                  &:before {
                    content: '';
                    position: absolute;
                    display: block;
                    top: 6px;
                    left: 0;
                    width: 10px;
                    height: 9px;
                    background: linear-gradient(
                      180deg,
                      #fe8321 0%,
                      #ff9925 100%
                    );
                    clip-path: path(
                      'M3.23358 6.92603L0.915578 4.31303L0.142578 5.18403L3.23358 8.66703L9.85658 1.20303L9.08358 0.332031L3.23358 6.92603Z'
                    );
                  }
                `}
                key={item}
              >
                {item}
              </li>
            ))}
          </ul>
        </dd>
      </dl>
    </PlanSection>
  );
};

type PlanSectionProps = PlanContentSectionProps & {
  prefix?: React.ReactNode;
  title: string;
  readText: React.ReactNode;
  const1: React.ReactNode;
  const2: React.ReactNode;
  feature: React.ReactNode;
  contactTo: string;
  caution: React.ReactNode;
  children: React.ReactNode;
  isAdvanced?: boolean;
};

const PlanSection: React.FC<PlanSectionProps> = ({
  className,
  as: Tag = 'h3',
  prefix,
  title,
  readText,
  const1,
  const2,
  feature,
  contactTo,
  caution,
  children,
  isAdvanced,
}) => {
  return (
    <section
      css={css`
        background: #fff;
        width: 100%;
        position: relative;
        border: 1px solid #d1d8dd;
        border-radius: 16px;
        padding-bottom: 30px;
        
        
        @media ${getScreen('pc')} {
          border-top-left-radius: 34px;
          border-top-right-radius: 34px;
        }
      `}
      className={className}
    >
      {prefix}
      <header
        css={css`
          height: 180px;
          position: relative;
          padding-top: 22px;
          background: #00c9be;
          border-top-left-radius: 34px;
          border-top-right-radius: 34px;
          border-bottom-left-radius: 415px 65px;
          border-bottom-right-radius: 415px 65px;
          padding-left: 10px;
          padding-right: 10px;
          
          @media ${getScreen('pc', '未満')} {
            height: 165px;
            padding-left: 20px;
            padding-right: 20px;
            border-top-left-radius: 15px;
            border-top-right-radius: 15px;
          }

          ${isAdvanced &&
          css`
            background: transparent linear-gradient(178deg, rgba(0, 201, 190, 1) 0%, rgba(0, 202, 191, 1) 34%, rgba(7, 219, 205, 1) 100%) 0% 0% no-repeat padding-box;
          `}
          
        `}
      >
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            overflow: hidden;
            border-top-left-radius: 15px;
            border-top-right-radius: 15px;
          `}
        />
        <p
          css={css`
            position: relative;
            max-width: 322px;
            background: #ffffff;
            border-radius: 8px;
            width: 100%;
            padding: 8px 0;
            margin: auto;
            text-align: center;
            font: normal normal bold 14px/22px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.28px;
            color: #5a5b6a;

            @media ${getScreen('pc', '未満')} {
              padding: 4px 0;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.28px;
            }

            strong {
              color: #fe8321;
              font-weight: inherit;
            }
          `}
        >
          {readText}
        </p>
        <Tag
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 68px;
            margin-top: 13px;
            text-align: center;
            font-size: 2.6rem;
            line-height: 1.3;
            font-weight: bold;
            letter-spacing: 0.52px;
            color: #ffffff;

            @media ${getScreen('pc', '未満')} {
              font-size: 2.2rem;
            }
          `}
        >
          {title}
        </Tag>
      </header>
      <div
        css={css`
          padding: 10px 0 20px;
        `}
      >
        <dl
          css={css`
            display: flex;
            justify-content: center;
            align-items: baseline;
            padding: 15px 0 5px;
            min-height: 70px;
            margin: auto;
            font: normal normal normal 14px/22px 'Noto Sans JP',sans-serif;
            color: #707070;
            dt {
              width: 90px;
            }
            dd {
              width: 140px;
              text-align: right;
              big {
                font-size: 28px;
                font-weight: bold;
              }
              span {
              }
              small {
                font-size: 10px;
                position: relative;
                top: -5px;
              }
            }
          `}
        >
          <dt>初期導入費用</dt>
          <dd>{const1}</dd>
        </dl>
        <dl
          css={css`
            display: flex;
            justify-content: center;
            align-items: baseline;
            margin: auto;
            padding: 15px 0 5px;
            min-height: 70px;
            font: normal normal normal 14px/22px 'Noto Sans JP',sans-serif;
            color: #707070;
            background-color: #F2F4F5;
            dt {
              width: 90px;
            }
            dd {
              width: 140px;
              text-align: right;
              big {
                font-size: 28px;
                font-weight: bold;
              }
              span {
              }
              small {
                font-size: 10px;
                position: relative;
                top: -5px;
              }
            }
          `}
        >
          <dt>月額費用</dt>
          <dd>{const2}</dd>
        </dl>
      </div>
      <div
        css={css`
          margin: auto;
          margin-bottom: 20px;
          width: 80%;
          display: flex;
          justify-content: center;
        `}
      >
        <ButtonLink to={contactTo} variant="plan-contact">
          このプランで相談する
        </ButtonLink>
      </div>
      <div
        css={css`
          padding: 0 24px;
        `}
      >
        {children}
      </div>
    </section>
  );
};

type BasicPlanFeatureCardProps = {
  className?: string;
  title: string;
  items: string[];
  caution?: React.ReactNode;
};

const BasicPlanFeatureCard: React.FC<BasicPlanFeatureCardProps> = ({
  className,
  title,
  items,
  caution,
}) => {
  return (
    <details
      css={css`
        border: 1px solid #e6e6e6;
        border-radius: 4px;
      `}
      className={className}
    >
      <summary
        css={css`
          list-style: none;

          &::-webkit-details-marker {
            display: none;
          }
        `}
      >
        <p
          css={css`
            font-weight: bold;
            font-size: 1.4rem;
            line-height: 2rem;
            letter-spacing: 0.28px;
            color: #00b3a7;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 12px;
            height: 46px;
            border: 1px solid transparent;
            outline: none;

            &::after {
              content: url({withPrefix('/images/plan_icon_arrow.svg')});
              transform-origin: 50% 56%;
              transition: 0.4s;
            }

            [open] &::after {
              transform: rotate(-180deg);
            }
          `}
        >
          {title}
        </p>
      </summary>
      <div
        css={css`
          padding: 10px 0 20px;

          [open] & {
            animation: ${fadeIn} 0.3s ease both;
          }
        `}
      >
        <ul
          css={css`
            list-style: none;
            text-align: left;
            padding: 0 20px;
          `}
        >
          {items.map((item, i) => (
            <li
              key={i}
              css={css`
                position: relative;
                padding-inline-start: 1.4em;
                font-size: 1.2rem;
                line-height: 2.4rem;
                letter-spacing: 0.12px;
                color: #444557;

                &:before {
                  content: '';
                  position: absolute;
                  top: 6px;
                  left: 0;
                  width: 10px;
                  height: 9px;
                  background-repeat: no-repeat;
                  background-image: url(${withPrefix('/images/list-check-icon.svg')});
                }
              `}
            >
              {item}
            </li>
          ))}
        </ul>
        {caution && (
          <p
            css={css`
              font: normal normal normal 11px/22px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.12px;
              color: #444557;
              padding: 0 30px;
              margin-top: 10px;
            `}
          >
            {caution}
          </p>
        )}
      </div>
    </details>
  );
};

const Support: React.FC<PlanContentSectionProps> = ({ className }) => {
  return (
    <div
      css={css`
        width: 100%;
        border: 1px solid #00c9be;
        border-radius: 16px;
        padding: 20px 30px;
        background: #f0fcfb 0% 0% no-repeat padding-box;

        @media ${getScreen('pc')} {
          padding: 40px 65px;
        }
      `}
      className={className}
    >
      <h2
        css={css`
          font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
          letter-spacing: 0.13px;
          color: #5a5b6a;
          margin-bottom: 24px;
          text-align: center;
        `}
      >
        さらにIPOやM&Aを考えている準備期間の中で、より専門的なサービスもご案内可能です。
      </h2>
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          flex-direction: column;
          max-width: 988px;
          margin: auto;
          @media ${getScreen('pc')} {
            flex-wrap: nowrap;
            flex-direction: inherit;
          }
          
          & > div {
            width: 100%;
            max-width: none;
            background-color: #fff;
            border: 1px solid #00b3a7;
            border-radius: 4px;
            @media ${getScreen('pc')} {
              width: calc(20% - 10px);
              max-width: 186px;
            }
            
            h3 {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 71px;
              font-size: 1.4rem;
              font-weight: bold;
              color: #00b3a7;
              text-align: center;
              border-bottom: 1px solid #d1d8dd;
              padding: 11px 10px;
            }
            
            & > div {
              display: flex;
              justify-content: center;
              padding: 20px 10px;
              
              @media ${getScreen('pc', '未満')} {
                display: block;
                padding: 0 15px;
              }
              
              ul {
                position: relative;
                padding: 0 2.5%;
                font: normal normal normal 12px/24px 'Noto Sans JP',sans-serif;
                letter-spacing: 0.12px;
              
                @media ${getScreen('pc', '未満')} {
                  width: 100%;
                  margin: auto;
                  padding: 20px 0;
                }
                
                li {
                  text-indent: -1em;
                  margin-left: 1em;
                  &::before {
                    content: "・";
                  }
                  
                  a {
                    color: #00B3A7;
                    font-weight: 500;
                    text-decoration: underline;
                  }
                }
              }
            }
          }
        `}
      >
      {
        [
          {
            title: <>IPO準備クラウド<br />オプション機能</>,
            list: [
              {
                text: 'ストックオプション機能',
                link: '',
              },
            ]
          },
          {
            title: 'BPO支援サービス',
            list: [
              {
                text: '規程類の作成',
                link: '',
              },
              {
                text: '業務フロー作成',
                link: 'https://uniforce.co.jp/bpo-consulting/',
              },
              {
                text: 'J-SOX3点セット作成',
                link: 'https://uniforce.co.jp/bpo-consulting/',
              },
              {
                text: 'BPO業務支援',
                link: 'https://uniforce.co.jp/bpo-consulting/',
              },
              {
                text: '内部監査室代行',
                link: '',
              },
              {
                text: '記帳代行',
                link: '',
              },
              {
                text: '各種申請書作成',
                link: '',
              },
              {
                text: '会計制度整備',
                link: '',
              },
              {
                text: '監査法人の紹介',
                link: '',
              },
            ]
          },
          {
            title: '資料作成サービス',
            list: [
              {
                text: '中期経営企画の作成支援',
                link: 'https://uniforce.co.jp/design/',
              },
              {
                text: 'エクイティ資料の作成',
                link: 'https://uniforce.co.jp/design/',
              },
              {
                text: 'Ⅰの部（決算開示資料）作成',
                link: '',
              },
              {
                text: 'Ⅱの部・各種説明資料作成',
                link: '',
              },
              {
                text: 'ピッチ資料の作成',
                link: 'https://uniforce.co.jp/design/',
              },
            ]
          },
          {
            title: '資金調達支援サービス',
            list: [
              {
                text: '資金調達支援',
                link: 'https://uniforce.co.jp/bpo-consulting/',
              },
              {
                text: '資本政策立案支援',
                link: 'https://uniforce.co.jp/bpo-consulting/',
              },
              {
                text: '上場時の株価のアドバイス',
                link: '',
              },
              {
                text: '株価算定',
                link: 'https://uniforce.co.jp/bpo-consulting/',
              },
              {
                text: '融資支援',
                link: '',
              },
              {
                text: '証券会社の紹介',
                link: '',
              },
            ]
          },
          {
            title: 'その他支援サービス',
            list: [
              {
                text: '資産管理会社設立サポート',
                link: '',
              },
              {
                text: 'コーポレートアイテム作成',
                link: '',
              },
              {
                text: 'リブランディング',
                link: '',
              },
            ]
          },
        ].map((item, i) => (
          <div key={i}>
            <h3>{item.title}</h3>
            <div>
              <ul>
                {item.list.map((listitem, index) => (
                  <li key={index}>
                    {listitem.link ? (
                      <a href={listitem.link} target="_blank" rel="noreferrer">
                        {listitem.text}
                      </a>
                    ) : (
                      <span>{listitem.text}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))
      }
      </div>
      <p 
        css={css`
          margin-top: 40px;
          font: normal normal 500 16px/24px 'Noto Sans JP', sans-serif;
          letter-spacing: 0.13px;
          text-align: center;
          color: #5A5B6A;
        `}
      >
        会社の状況に合わせたプランで徹底サポートいたします
      </p>
      <div
        css={css`
          margin-top: 25px;
          display: flex;
          justify-content: center;
        `}
      >
        <ButtonLink to={pagesPath.contact.$url()} variant="plan-contact">
          お問い合わせ
        </ButtonLink>
      </div>
    </div>
  );
};
